import { Type } from '@angular/core';
import { Routes } from '@angular/router';
import { adminAuthGuard, guestGuard } from './core/guards/admin-auth.guard';
import { landingGuard, leadDeepLinkGuard, requirePermission } from './core/guards/permission.guard';
import { recoverFromLazyChunkFailure } from './core/errors/lazy-chunk-recovery';

function loadWithRecovery<TModule, TResult>(
  loader: () => Promise<TModule>,
  selector: (module: TModule) => TResult
): (() => Promise<TResult>) {
  return () =>
    loader()
      .then(selector)
      .catch(error => {
        if (!recoverFromLazyChunkFailure(error)) {
          throw error;
        }

        return new Promise<TResult>(() => undefined);
      });
}

function loadComponentWithRecovery<TModule, TComponent>(
  loader: () => Promise<TModule>,
  selector: (module: TModule) => Type<TComponent>
): (() => Promise<Type<TComponent>>) {
  return loadWithRecovery(loader, selector);
}

export const routes: Routes = [
  {
    path: 'login',
    canActivate: [guestGuard],
    loadComponent: loadComponentWithRecovery(
      () => import('./features/auth/login.page'),
      (m) => m.LoginPage
    )
  },
  {
    path: '',
    loadComponent: loadComponentWithRecovery(
      () => import('./shared/layout/shell.component'),
      (m) => m.ShellComponent
    ),
    canActivate: [adminAuthGuard],
    children: [
      {
        // Redirects to the first page the operator may view; renders the no-access page when there is none.
        path: '',
        pathMatch: 'full',
        canMatch: [landingGuard],
        loadComponent: loadComponentWithRecovery(
          () => import('./features/no-access/no-access.page'),
          (m) => m.NoAccessPage
        )
      },
      {
        path: 'dashboard',
        canMatch: [requirePermission('analytics')],
        data: { titleKey: 'admin.shell.nav.dashboard', crumbs: [{ label: 'admin.shell.nav.dashboard' }] },
        loadComponent: loadComponentWithRecovery(
          () => import('./features/dashboard/dashboard.page'),
          (m) => m.DashboardPage
        )
      },
      {
        path: 'tenants/provision',
        canMatch: [requirePermission('tenants', 'manage')],
        data: { titleKey: 'admin.tenants.provision.title', crumbs: [{ label: 'admin.shell.nav.tenants', path: '/tenants' }, { label: 'admin.tenants.provision.title' }] },
        loadComponent: loadComponentWithRecovery(
          () => import('./features/tenants/tenant-provision.page'),
          (m) => m.TenantProvisionPage
        )
      },
      {
        path: 'tenants',
        canMatch: [requirePermission('tenants')],
        data: { titleKey: 'admin.shell.nav.tenants', crumbs: [{ label: 'admin.shell.nav.tenants' }] },
        loadComponent: loadComponentWithRecovery(
          () => import('./features/tenants/tenants-list.page'),
          (m) => m.TenantsListPage
        )
      },
      {
        path: 'tenants/:id',
        canMatch: [requirePermission('tenants')],
        data: { titleKey: 'admin.tenants.detail.title', crumbs: [{ label: 'admin.shell.nav.tenants', path: '/tenants' }, { label: 'admin.tenants.detail.title' }] },
        loadComponent: loadComponentWithRecovery(
          () => import('./features/tenants/tenant-detail.page'),
          (m) => m.TenantDetailPage
        )
      },
      {
        path: 'plans',
        canMatch: [requirePermission('plans')],
        data: { titleKey: 'admin.shell.nav.plans', crumbs: [{ label: 'admin.shell.nav.plans' }] },
        loadComponent: loadComponentWithRecovery(
          () => import('./features/plans/plans.page'),
          (m) => m.PlansPage
        )
      },
      {
        // Each child route carries its own permission (overview: tenants, plans: plans).
        path: 'billing',
        loadChildren: loadWithRecovery(
          () => import('./features/billing/billing.routes'),
          (m) => m.BILLING_ROUTES
        )
      },
      {
        path: 'promo-codes',
        canMatch: [requirePermission('promoCodes')],
        data: { titleKey: 'admin.shell.nav.promoCodes', crumbs: [{ label: 'admin.shell.nav.promoCodes' }] },
        loadComponent: loadComponentWithRecovery(
          () => import('./features/promo-codes/promo-codes.page'),
          (m) => m.PromoCodesPage
        )
      },
      {
        path: 'leads',
        canMatch: [requirePermission('leads')],
        data: { titleKey: 'admin.shell.nav.leads', crumbs: [{ label: 'admin.shell.nav.leads' }] },
        loadComponent: loadComponentWithRecovery(
          () => import('./features/leads/leads-list.page'),
          (m) => m.LeadsListPage
        )
      },
      {
        // Deep link from the new-lead notifications: opens the list with that lead's drawer, and the URL stays shareable.
        // A guard rather than `redirectTo`: Angular forbids `redirectTo` together with `canMatch`, and the permission check must come first.
        path: 'leads/:id',
        canMatch: [leadDeepLinkGuard],
        // Never rendered: the guard always redirects (Angular needs the route to name a target).
        children: []
      },
      {
        path: 'operators',
        canMatch: [requirePermission('operators')],
        data: { titleKey: 'admin.shell.nav.operators', crumbs: [{ label: 'admin.shell.nav.operators' }] },
        loadComponent: loadComponentWithRecovery(
          () => import('./features/operators/operators-list.page'),
          (m) => m.OperatorsListPage
        )
      }
    ]
  }
];
